/**
 * Central route → access policy for the NGO portal: the SINGLE source of truth for "who may see which
 * `/ngos` route".
 *
 * Consumed by the `/ngos` layout guard (routes/ngos/route.tsx), which resolves the session ONCE at the
 * root of the NGO area and redirects before any `ngo.*` query mounts — so the gated query never fires
 * and no route component carries its own `<Navigate>`.
 *
 * Pure + dependency-free (no router, tRPC or DOM) so it's unit-testable: callers supply the resolved
 * `getNgoSession` result plus the current route id and pathname.
 */
import { ngoLoginRedirectPath } from './ngo-session';

/**
 * Route-id prefixes reachable WITHOUT an `ngo-session` cookie. Everything else under `/ngos` requires
 * one. These pages also render bare — they own their full-page layout and get no sidebar chrome.
 *
 * Keyed off the route id (not the pathname) because the router serves the NGO portal under an internal
 * `/ngos` prefix that the browser URL omits (see `rewrite` in router.tsx); the id is stable either way.
 */
export const PUBLIC_NGO_ROUTE_PREFIXES = ['/ngos/login', '/ngos/select-ngo', '/ngos/onboarding', '/ngos/auth/callback'];

/** The resolved `auth.getNgoSession` shape — null when the session cookie is missing or expired. */
export type NgoSession = { ngoId: string } | null;

/** The redirect the guard should perform, or null to render normally. */
export type NgoRedirect = { to: '/ngos/login'; search: { redirect: string | undefined } };

/** True on the public (chrome-less) NGO pages: login, NGO selection, onboarding, the magic-link callback. */
export function isPublicNgoRoute(routeId: string): boolean {
	return PUBLIC_NGO_ROUTE_PREFIXES.some((prefix) => routeId.startsWith(prefix));
}

/**
 * Resolve the redirect the current NGO route should perform for this session, or null to render it.
 *
 * `pathname` is only read to build the post-login target, so callers should pass the pathname as it was
 * on the guard's FIRST render (see the snapshot in routes/ngos/route.tsx).
 */
export function resolveNgoRouteAccess(input: {
	routeId: string;
	session: NgoSession;
	pathname: string;
}): NgoRedirect | null {
	const { routeId, session, pathname } = input;
	if (isPublicNgoRoute(routeId)) return null;
	if (session) return null;
	return { to: '/ngos/login', search: { redirect: ngoLoginRedirectPath(pathname) } };
}
